import React, { ElementRef, useRef } from 'react';

import { Box, Flex, Heading, Hide, Show, Text, useMediaQuery, useQuery } from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';

import { Button } from '@/components/atoms';
import { Slider } from '@/components/functional';
import { SliderControls } from '@/components/functional/SliderControls/SliderControls';
import { EbookCard, FreeEbooksDoodle, LibraryAllBackground } from '@/components/sections/library';
import { LibraryFragment } from '@/components/sections/library/typings';
import { AnimateWhenInViewWrapper, SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { GeneralPath } from '@/utils/models';

type OurGuidesSectionProps = {
  ebooks: LibraryFragment[];
  showMore?: boolean;
};

const NUMBER_OF_EBOOKS_IN_NESTED_SECTION = 3;

export const OurGuidesSection = ({ ebooks, showMore }: OurGuidesSectionProps) => {
  const { t } = useTranslation();
  const [isBelowXS] = useMediaQuery(useQuery({ below: 'xs' }));

  const sliderRef = useRef<ElementRef<typeof Slider>>(null);
  const ebookList = showMore && ebooks.length > 3 ? ebooks.slice(0, NUMBER_OF_EBOOKS_IN_NESTED_SECTION) : ebooks;

  return (
    <SectionWrapper>
      <SectionContentWrapper>
        <LibraryAllBackground showMore={showMore} />
        <Flex flexDir="column" alignItems="center">
          <Heading
            variant={{
              base: 'component-heading-large/xl',
              xs: 'component-heading-large/2xl',
              sm: 'component-heading-large/3xl',
            }}
            color="purple.500"
            mb={6}
          >
            {t('ourGuides.title')}
          </Heading>
          <Text
            as="h3"
            variant={{
              base: 'component-text/xl',
              sm: 'component-text/2xl',
              md: 'text-3xl/lineHeight-9/font-normal',
            }}
            color="purple.400"
            textAlign="center"
            mb={{
              base: 0,
              xs: 4,
            }}
          >
            {t('ourGuides.desc1')}
            <br /> {t('ourGuides.desc2')}
          </Text>
          <Show below="xs">
            <Flex w="100%" justifyContent="flex-end" mr={-4} mb={14}>
              <FreeEbooksDoodle w="165px" />
            </Flex>
          </Show>
          {!showMore && (
            <>
              <Hide above="sm">
                <Hide below="xs">
                  <SliderControls
                    onSlidePrev={() => sliderRef.current?.onPrev()}
                    onSlideNext={() => sliderRef.current?.onNext()}
                    noUnderline={true}
                    w="100%"
                    mb={-4}
                  />
                  <Box maxW="100%">
                    <Slider
                      gapPX={1}
                      ref={sliderRef}
                      py={14}
                      sx={{
                        marginX: {
                          base: -8,
                          lg: -20,
                          '3xl': -80,
                        },
                        width: {
                          base: 'calc(100% + 64px)',
                          lg: 'calc(100% + 160px)',
                          '3xl': 'calc(100% + 640px)',
                        },
                        '> a': {
                          paddingLeft: {
                            base: 14,
                          },
                        },
                        ' > a:last-of-type': {
                          marginRight: 4,
                        },
                      }}
                    >
                      {ebooks.map((ebook) => {
                        if (!ebook) return null;
                        return <EbookCard key={ebook?.slug} {...ebook} />;
                      })}
                    </Slider>
                  </Box>
                </Hide>
              </Hide>

              <Show above="sm">
                <Flex
                  justifyContent="space-between"
                  gap={{
                    sm: 8,
                    md: 10,
                    lg: 20,
                    xl: 28,
                  }}
                  py={14}
                >
                  {ebooks.map((ebook) => {
                    if (!ebook) return null;
                    return <EbookCard key={ebook?.slug} {...ebook} />;
                  })}
                </Flex>
              </Show>
            </>
          )}
          {(isBelowXS || showMore) && (
            <Flex
              flexDir={{
                base: 'column',
                sm: 'row',
              }}
              mt={{
                base: 0,
                xs: 20,
              }}
              w="100%"
              justifyContent={{
                base: 'center',
                xs: 'space-between',
              }}
              alignItems={{
                base: 'center',
                sm: 'flex-start',
              }}
              gap={{
                base: 12,
                xs: 20,
                sm: 12,
                md: 4,
              }}
              px={{
                base: 0,
                sm: 8,
              }}
              maxW="1200px"
            >
              {ebookList.map((ebook) => {
                if (!ebook) return null;
                return (
                  <AnimateWhenInViewWrapper key={ebook?.slug}>
                    <EbookCard {...ebook} />
                  </AnimateWhenInViewWrapper>
                );
              })}
            </Flex>
          )}
        </Flex>
        {showMore && (
          <Flex mt={14} mb={-4} justifyContent="center">
            <Link href={GeneralPath.LIBRARY}>
              <Button
                label={t('ourGuides.seeMore')}
                variant="outline"
                w={{
                  base: '166px',
                  sm: '266px',
                  md: '411px',
                }}
                size="lg"
              />
            </Link>
          </Flex>
        )}
      </SectionContentWrapper>
    </SectionWrapper>
  );
};
